import { useState, useEffect, useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import { httpsCallable } from 'firebase/functions'
import { isUserEditor, isUserAdmin } from '../firebase/auth'
import { functions } from '../firebase/config'
import {
  createDevSecOpsTopic,
  subscribeToDevSecOpsTopics,
  updateDevSecOpsTopic,
  deleteDevSecOpsTopic,
  markDevSecOpsTopicAsUsed,
  markDevSecOpsTopicAsUnused
} from '../firebase/devsecopsTopics'
import './DevSecOpsWebsiteArticles.css'

// Fixed categories for DevSecOps (not the Filesig products list)
const DEVSECOPS_CATEGORIES = ['Security', 'Developer', 'Operations']

const DevSecOpsWebsiteArticles = ({ user }) => {
  const [isEditor, setIsEditor] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)

  const [topics, setTopics] = useState([])
  const [showTopicForm, setShowTopicForm] = useState(false)
  const [editingTopic, setEditingTopic] = useState(null)
  const [topicText, setTopicText] = useState('')
  const [topicCategory, setTopicCategory] = useState('')
  const [topicError, setTopicError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState(null)
  const [importCategory, setImportCategory] = useState('')
  const [importText, setImportText] = useState('')
  const [showTextImport, setShowTextImport] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [topicToDelete, setTopicToDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [r2TestLoading, setR2TestLoading] = useState(false)
  const [r2TestMessage, setR2TestMessage] = useState(null)

  // Articles tab state
  const [activeTab, setActiveTab] = useState('topics')
  const [selectedArticleTopic, setSelectedArticleTopic] = useState(null)
  const [selectedArticleIds, setSelectedArticleIds] = useState(new Set())
  const [articleEditContent, setArticleEditContent] = useState('')
  const [articleSaving, setArticleSaving] = useState(false)
  const [articlePreviewMode, setArticlePreviewMode] = useState('formatted') // 'formatted' | 'raw'
  const [approving, setApproving] = useState(false)

  useEffect(() => {
    const checkUserStatus = async () => {
      if (user) {
        const [editorStatus, adminStatus] = await Promise.all([
          isUserEditor(user),
          isUserAdmin(user)
        ])
        setIsEditor(editorStatus)
        setIsAdmin(adminStatus)
      }
      setLoading(false)
    }
    checkUserStatus()
  }, [user])

  useEffect(() => {
    if (isEditor) {
      const unsubscribeTopics = subscribeToDevSecOpsTopics((topicsList) => {
        setTopics(topicsList)
        setCurrentPage((prevPage) => {
          const totalPages = Math.ceil(topicsList.length / itemsPerPage)
          return totalPages > 0 && prevPage > totalPages ? 1 : prevPage
        })
      })
      return () => unsubscribeTopics()
    }
  }, [isEditor, itemsPerPage])

  const stats = useMemo(() => {
    const total = topics.length
    return {
      total,
      unprocessed: topics.filter(t => t.status === 'unprocessed').length,
      preProcessing: topics.filter(t => t.status === 'pre-processing').length,
      available: topics.filter(t => t.status === 'available').length,
      approved: topics.filter(t => t.status === 'approved').length,
      published: topics.filter(t => t.status === 'published').length,
      rejected: topics.filter(t => t.status === 'rejected').length,
      error: topics.filter(t => t.status === 'error').length
    }
  }, [topics])

  const totalTopics = stats.total
  const unprocessedTopics = stats.unprocessed
  const preProcessingTopics = stats.preProcessing
  const availableTopics = stats.available
  const approvedTopics = stats.approved
  const publishedTopics = stats.published
  const rejectedTopics = stats.rejected
  const errorTopics = stats.error

  const totalPages = Math.ceil(totalTopics / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentPageTopics = topics.slice(startIndex, endIndex)

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleTopicFormChange = (e) => {
    const { name, value } = e.target
    if (name === 'topicText') setTopicText(value)
    else if (name === 'topicCategory') setTopicCategory(value)
    setTopicError(null)
  }

  const handleTopicSubmit = async (e) => {
    e.preventDefault()
    setTopicError(null)
    if (!topicText.trim()) {
      setTopicError('Topic text is required')
      return
    }
    setSubmitting(true)
    try {
      const categoryId = topicCategory || null
      if (editingTopic) {
        await updateDevSecOpsTopic(editingTopic.id, {
          text: topicText.trim(),
          category: categoryId
        })
        setEditingTopic(null)
      } else {
        await createDevSecOpsTopic(topicText.trim(), categoryId)
      }
      setTopicText('')
      setTopicCategory('')
      setShowTopicForm(false)
    } catch (err) {
      console.error('Error saving topic:', err)
      setTopicError(err.message || 'Failed to save topic. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleStartEdit = (topic) => {
    if (topic.status === 'used') {
      setTopicError('Cannot edit a topic that has been used')
      return
    }
    setEditingTopic(topic)
    setTopicText(topic.text)
    setTopicCategory(topic.category || '')
    setShowTopicForm(true)
    setTopicError(null)
  }

  const handleCancelEdit = () => {
    setEditingTopic(null)
    setTopicText('')
    setTopicCategory('')
    setShowTopicForm(false)
    setTopicError(null)
  }

  const handleDeleteTopic = (topic) => {
    setTopicToDelete(topic)
    setShowDeleteModal(true)
  }

  const confirmDeleteTopic = async () => {
    if (!topicToDelete) return
    setDeleting(true)
    try {
      await deleteDevSecOpsTopic(topicToDelete.id)
      setShowDeleteModal(false)
      setTopicToDelete(null)
    } catch (err) {
      console.error('Error deleting topic:', err)
      alert('Failed to delete topic. Please try again.')
    } finally {
      setDeleting(false)
    }
  }

  const cancelDeleteTopic = () => {
    setShowDeleteModal(false)
    setTopicToDelete(null)
  }

  const handleMarkAsUsed = async (topicId) => {
    try {
      await markDevSecOpsTopicAsUsed(topicId)
    } catch (err) {
      console.error('Error marking topic as used:', err)
      alert('Failed to mark topic as used. Please try again.')
    }
  }

  const handleMarkAsUnused = async (topicId) => {
    if (!window.confirm('Are you sure you want to mark this topic as unused? This will allow it to be reused.')) return
    try {
      await markDevSecOpsTopicAsUnused(topicId)
    } catch (err) {
      console.error('Error marking topic as unused:', err)
      alert('Failed to mark topic as unused. Please try again.')
    }
  }

  const processTopicsImport = async (topicsText) => {
    if (!importCategory) {
      setTopicError('Please select a category before importing topics.')
      return
    }
    setImporting(true)
    setImportResult(null)
    setTopicError(null)
    try {
      const lines = topicsText.split('\n')
      const importedTopics = lines
        .map(line => line.replace(/\s+$/, ''))
        .filter(line => line.length > 0)

      if (importedTopics.length === 0) {
        setImportResult({
          success: false,
          message: 'No valid topics found. Each topic should be on a separate line.'
        })
        setImporting(false)
        return
      }

      const existingTopicsMap = new Map()
      topics.forEach(topic => {
        const key = `${topic.text.trim().toLowerCase()}|${topic.category || null}`
        existingTopicsMap.set(key, true)
      })

      const uniqueTopics = []
      const duplicates = []
      const seenInFile = new Set()
      const importCategoryId = importCategory || null

      for (const text of importedTopics) {
        const normalized = text.trim().toLowerCase()
        const key = `${normalized}|${importCategoryId}`
        if (seenInFile.has(normalized)) {
          duplicates.push(text)
          continue
        }
        seenInFile.add(normalized)
        if (existingTopicsMap.has(key)) {
          duplicates.push(text)
          continue
        }
        uniqueTopics.push(text)
      }

      let importedCount = 0
      let failedCount = 0
      const categoryId = importCategory || null

      for (const text of uniqueTopics) {
        try {
          await createDevSecOpsTopic(text, categoryId)
          importedCount++
        } catch (err) {
          console.error('Error importing topic:', text, err)
          failedCount++
        }
      }

      const duplicateCount = duplicates.length
      setImportResult({
        success: true,
        imported: importedCount,
        duplicates: duplicateCount,
        failed: failedCount,
        message: `Import completed: ${importedCount} topics imported${duplicateCount > 0 ? `, ${duplicateCount} duplicates skipped` : ''}${failedCount > 0 ? `, ${failedCount} failed` : ''}.`
      })
      setTimeout(() => setImportResult(null), 5000)
      if (showTextImport) setImportText('')
    } catch (err) {
      console.error('Error importing topics:', err)
      setImportResult({ success: false, message: 'Failed to import topics. Please try again.' })
    } finally {
      setImporting(false)
    }
  }

  const handleFileImport = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    e.target.value = ''
    try {
      const text = await file.text()
      await processTopicsImport(text)
    } catch (err) {
      console.error('Error reading file:', err)
      setImportResult({
        success: false,
        message: 'Failed to read file. Please ensure it is a valid text file.'
      })
    }
  }

  const handleTextImport = async () => {
    if (!importText.trim()) {
      setTopicError('Please enter at least one topic.')
      return
    }
    await processTopicsImport(importText)
  }

  const handleImportTextChange = (e) => {
    const value = e.target.value.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    const lines = value.split('\n')
    if (lines.length <= 100) {
      setImportText(value)
      setTopicError(null)
    } else {
      setTopicError('Maximum 100 lines allowed. Please reduce the number of topics.')
    }
  }

  const handleImportTextPaste = async (e) => {
    e.preventDefault()
    const pastedText = (e.clipboardData || window.clipboardData).getData('text')
    let normalizedText = pastedText
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\u2028/g, '\n')
      .replace(/\u2029/g, '\n')
    const textarea = e.target
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const newText = importText.substring(0, start) + normalizedText + importText.substring(end)
    const lines = newText.split('\n')
    if (lines.length <= 100) {
      setImportText(newText)
      setTopicError(null)
      setTimeout(() => {
        textarea.setSelectionRange(start + normalizedText.length, start + normalizedText.length)
      }, 0)
    } else {
      setTopicError('Maximum 100 lines allowed. Please reduce the number of topics.')
    }
  }

  const handleTestR2Upload = async () => {
    setR2TestMessage(null)
    setR2TestLoading(true)
    try {
      const uploadTestFileToR2 = httpsCallable(functions, 'uploadTestFileToR2')
      const result = await uploadTestFileToR2()
      const data = result?.data
      if (data?.success) {
        setR2TestMessage({ success: true, text: data.message })
      } else {
        setR2TestMessage({ success: false, text: data?.message || 'R2 upload failed' })
      }
      setTimeout(() => setR2TestMessage(null), 6000)
    } catch (err) {
      const msg = err?.message || err?.code || 'Failed to call R2 test'
      setR2TestMessage({ success: false, text: msg })
      setTimeout(() => setR2TestMessage(null), 6000)
    } finally {
      setR2TestLoading(false)
    }
  }

  // Articles tab: available topics (status === 'available')
  const availableTopicsList = useMemo(
    () => topics.filter(t => t.status === 'available'),
    [topics]
  )

  const handleSelectArticleTopic = (topic) => {
    setSelectedArticleTopic(topic)
    setArticleEditContent(topic.procdoutput || topic.raw || '')
    setArticlePreviewMode('formatted')
  }

  const handleArticleEditChange = (e) => {
    setArticleEditContent(e.target.value)
  }

  const handleSaveArticle = async () => {
    if (!selectedArticleTopic) return
    setArticleSaving(true)
    try {
      await updateDevSecOpsTopic(selectedArticleTopic.id, { procdoutput: articleEditContent })
    } catch (err) {
      console.error('Error saving article:', err)
      alert('Failed to save article. Please try again.')
    } finally {
      setArticleSaving(false)
    }
  }

  // Approval rule: exactly 3 articles, 1 from each category (Developer, Security, Operations)
  const REQUIRED_CATEGORIES = ['Developer', 'Security', 'Operations']

  const handleToggleArticleSelection = (topicId) => {
    const topic = availableTopicsList.find(t => t.id === topicId)
    setSelectedArticleIds(prev => {
      const next = new Set(prev)
      if (next.has(topicId)) {
        next.delete(topicId)
      } else {
        // Max 3, and only 1 per category (Developer, Security, Operations)
        if (next.size >= 3) return prev
        if (topic?.category && REQUIRED_CATEGORIES.includes(topic.category)) {
          const existing = availableTopicsList.find(t => next.has(t.id) && t.category === topic.category)
          if (existing) next.delete(existing.id)
        }
        next.add(topicId)
      }
      return next
    })
  }
  const isApprovalSelectionValid = useMemo(() => {
    if (selectedArticleIds.size !== 3) return false
    const selectedTopics = availableTopicsList.filter(t => selectedArticleIds.has(t.id))
    const categories = selectedTopics.map(t => t.category).filter(Boolean)
    return REQUIRED_CATEGORIES.every(cat => categories.includes(cat))
  }, [selectedArticleIds, availableTopicsList])

  const handleSelectOnePerCategory = () => {
    const byCategory = {}
    for (const topic of availableTopicsList) {
      if (!topic.category || !REQUIRED_CATEGORIES.includes(topic.category)) continue
      if (!byCategory[topic.category]) byCategory[topic.category] = topic.id
    }
    const ids = Object.values(byCategory)
    if (ids.length === 3) {
      setSelectedArticleIds(new Set(ids))
    } else {
      setSelectedArticleIds(new Set())
    }
  }

  const handleApproveSelected = async () => {
    if (!isApprovalSelectionValid) {
      alert('Please select exactly 3 topics: one from Developer, one from Security, and one from Operations.')
      return
    }
    setApproving(true)
    try {
      await Promise.all(
        Array.from(selectedArticleIds).map(id => updateDevSecOpsTopic(id, { status: 'approved' }))
      )
      setSelectedArticleIds(new Set())
    } catch (err) {
      console.error('Error approving topics:', err)
      alert('Failed to approve topics. Please try again.')
    } finally {
      setApproving(false)
    }
  }

  if (loading) {
    return (
      <div className="devsecops-articles-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  if (!isEditor) {
    return (
      <div className="devsecops-articles-container">
        <div className="devsecops-articles-access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="devsecops-articles-container">
      <div className="devsecops-articles-header">
        <h1>DevSecOps Website Articles</h1>
        <p className="devsecops-articles-subtitle">Manage DevSecOps website article topics (separate from Filesig articles)</p>
      </div>

      <div className="devsecops-articles-tabs">
        <button
          className={`devsecops-articles-tab ${activeTab === 'topics' ? 'active' : ''}`}
          onClick={() => setActiveTab('topics')}
        >
          Topics
        </button>
        <button
          className={`devsecops-articles-tab ${activeTab === 'articles' ? 'active' : ''}`}
          onClick={() => setActiveTab('articles')}
        >
          Articles
          {availableTopics > 0 && (
            <span className="devsecops-tab-badge">{availableTopics}</span>
          )}
        </button>
      </div>

      <div className="devsecops-articles-content">
        {activeTab === 'topics' && (
        <div className="topics-section">
          <div className="topics-header">
            <h2>Topics</h2>
            <div className="topics-header-actions">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginRight: '0.75rem' }}>
                <label htmlFor="import-category" style={{ fontSize: '14px', color: '#666' }}>
                  Import Category: <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <select
                  id="import-category"
                  value={importCategory}
                  onChange={(e) => {
                    setImportCategory(e.target.value)
                    setTopicError(null)
                  }}
                  disabled={importing}
                  required
                  style={{
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: importCategory ? '1px solid #ddd' : '1px solid #dc3545',
                    fontSize: '14px',
                    minWidth: '150px'
                  }}
                >
                  <option value="">Select category...</option>
                  {DEVSECOPS_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
              <button
                className="btn btn-secondary"
                onClick={() => setShowTextImport(!showTextImport)}
                disabled={importing}
                style={{ marginRight: '0.75rem' }}
              >
                {showTextImport ? '📁 Import from File' : '📝 Import from Text'}
              </button>
              {!showTextImport ? (
                <label
                  className="btn btn-secondary"
                  htmlFor="devsecops-topic-import-file"
                  style={{
                    marginRight: '0.75rem',
                    opacity: importCategory ? 1 : 0.6,
                    cursor: importCategory ? 'pointer' : 'not-allowed',
                    position: 'relative'
                  }}
                  title={!importCategory ? 'Please select a category first' : ''}
                >
                  {importing ? 'Importing...' : '📥 Import Topics'}
                  <input
                    id="devsecops-topic-import-file"
                    type="file"
                    accept=".txt,.text"
                    onChange={handleFileImport}
                    disabled={importing || !importCategory}
                    style={{ display: 'none' }}
                  />
                </label>
              ) : (
                <button
                  className="btn btn-secondary"
                  onClick={handleTextImport}
                  disabled={importing || !importCategory || !importText.trim()}
                  style={{
                    marginRight: '0.75rem',
                    opacity: (importCategory && importText.trim()) ? 1 : 0.6,
                    cursor: (importCategory && importText.trim()) ? 'pointer' : 'not-allowed'
                  }}
                >
                  {importing ? 'Importing...' : '📥 Import Topics'}
                </button>
              )}
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowTopicForm(!showTopicForm)
                  if (showTopicForm) handleCancelEdit()
                }}
                disabled={submitting || importing}
              >
                {showTopicForm ? 'Cancel' : '+ Add Topic'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleTestR2Upload}
                disabled={r2TestLoading || submitting || importing}
                title="Upload a test text file to R2 bucket devsecops101-articles"
                style={{ marginLeft: '0.5rem' }}
              >
                {r2TestLoading ? 'Uploading…' : 'Test R2'}
              </button>
            </div>
          </div>
          {r2TestMessage && (
            <div
              className={r2TestMessage.success ? 'import-result success' : 'import-result error'}
              style={{ marginTop: '0.75rem' }}
            >
              {r2TestMessage.text}
            </div>
          )}

          {showTextImport && (
            <div className="text-import-container" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
              <div className="form-group">
                <label htmlFor="import-text">
                  Enter Topics (one per line, max 100 lines)
                  <span className="form-help-text">Paste or type your topics here, one topic per line</span>
                </label>
                <textarea
                  id="import-text"
                  value={importText}
                  onChange={handleImportTextChange}
                  onPaste={handleImportTextPaste}
                  placeholder="Enter topics, one per line..."
                  rows={10}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    fontFamily: 'inherit',
                    fontSize: '14px',
                    resize: 'vertical',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word'
                  }}
                  disabled={importing}
                />
                <div style={{ marginTop: '0.5rem', fontSize: '12px', color: '#666' }}>
                  {importText.split('\n').filter(line => line.trim()).length} / 100 lines
                </div>
              </div>
            </div>
          )}

          {importResult && (
            <div className={`import-result ${importResult.success ? 'success' : 'error'}`}>
              {importResult.message}
            </div>
          )}

          <div className="topics-stats" key={`stats-${totalTopics}-${unprocessedTopics}-${availableTopics}-${publishedTopics}`}>
            <div className="stat-item">
              <span className="stat-label">Total:</span>
              <span className="stat-value">{totalTopics}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Unprocessed:</span>
              <span className="stat-value unprocessed">{unprocessedTopics}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Pre-Processing:</span>
              <span className="stat-value">{preProcessingTopics}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Available:</span>
              <span className="stat-value available">{availableTopics}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Approved:</span>
              <span className="stat-value approved">{approvedTopics}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Published:</span>
              <span className="stat-value approved">{publishedTopics}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Rejected:</span>
              <span className="stat-value rejected">{rejectedTopics}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Error:</span>
              <span className="stat-value rejected">{errorTopics}</span>
            </div>
          </div>

          {showTopicForm && (
            <div className="topic-form-container">
              <form onSubmit={handleTopicSubmit} className="topic-form">
                <div className="form-group">
                  <label htmlFor="topic-text">Topic Text *</label>
                  <textarea
                    id="topic-text"
                    name="topicText"
                    value={topicText}
                    onChange={handleTopicFormChange}
                    placeholder="Enter topic text"
                    rows="3"
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="topic-category">Category</label>
                  <select
                    id="topic-category"
                    name="topicCategory"
                    value={topicCategory}
                    onChange={handleTopicFormChange}
                    disabled={submitting}
                  >
                    <option value="">No category</option>
                    {DEVSECOPS_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                {topicError && <div className="form-error">{topicError}</div>}
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Saving...' : editingTopic ? 'Update Topic' : 'Create Topic'}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="topics-list">
            {topics.length === 0 ? (
              <div className="empty-state">
                <p>No topics yet. Click "Add Topic" to create one.</p>
              </div>
            ) : (
              <>
                <table className="topics-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Topic Text</th>
                      <th>Category</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPageTopics.map((topic) => {
                      const status = topic.status || 'unprocessed'
                      return (
                        <tr key={topic.id} className={status === 'used' ? 'used' : ''}>
                          <td>
                            {status === 'used' ? (
                              <span className="status-badge used-badge">Used</span>
                            ) : status === 'available' ? (
                              <span className="status-badge available-badge">Available</span>
                            ) : status === 'approved' ? (
                              <span className="status-badge approved-badge">Approved</span>
                            ) : status === 'rejected' ? (
                              <span className="status-badge rejected-badge">Rejected</span>
                            ) : status === 'error' ? (
                              <span className="status-badge rejected-badge">Error</span>
                            ) : status === 'pre-processing' ? (
                              <span className="status-badge">Pre-Processing</span>
                            ) : status === 'unprocessed' ? (
                              <span className="status-badge unprocessed-badge">Unprocessed</span>
                            ) : (
                              <span className="status-badge" title={status}>{status || 'Unprocessed'}</span>
                            )}
                          </td>
                          <td className="topic-text-cell">{topic.text}</td>
                          <td className="topic-category-cell">
                            {topic.category ? (
                              topic.category
                            ) : (
                              <span style={{ color: '#999', fontStyle: 'italic' }}>No category</span>
                            )}
                          </td>
                          <td className="topic-date-cell">
                            {topic.createdAt && topic.createdAt.toDate
                              ? new Date(topic.createdAt.toDate()).toLocaleDateString()
                              : 'N/A'}
                          </td>
                          <td className="topic-actions-cell">
                            <div className="topic-actions">
                              {status !== 'used' && (
                                <button
                                  className="btn-icon"
                                  onClick={() => handleStartEdit(topic)}
                                  title="Edit topic"
                                >
                                  ✏️
                                </button>
                              )}
                              {isAdmin && (
                                <>
                                  {status === 'used' && (
                                    <button
                                      className="btn-icon"
                                      onClick={() => handleMarkAsUnused(topic.id)}
                                      title="Mark as available (Admin only)"
                                    >
                                      ↶
                                    </button>
                                  )}
                                  <button
                                    className="btn-icon"
                                    onClick={() => handleDeleteTopic(topic)}
                                    title="Delete topic (Admin only)"
                                  >
                                    🗑️
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>

                {totalPages > 1 && (
                  <div className="topics-pagination">
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <div className="pagination-info">
                      Page {currentPage} of {totalPages}
                      <span className="pagination-count">
                        (Showing {startIndex + 1}-{Math.min(endIndex, totalTopics)} of {totalTopics})
                      </span>
                    </div>
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        )}

        {activeTab === 'articles' && (
          <div className="devsecops-articles-tab-content">
            <div className="articles-sidebar">
              <div className="articles-sidebar-header">
                <h3>Available Topics</h3>
                {availableTopicsList.length > 0 && (
                  <>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={handleSelectOnePerCategory}
                      title="Select one topic from each category (Developer, Security, Operations)"
                    >
                      Select one per category
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleApproveSelected}
                      disabled={approving || !isApprovalSelectionValid}
                      title={!isApprovalSelectionValid ? 'Select exactly 3 topics: one from Developer, Security, and Operations' : ''}
                    >
                      {approving ? 'Approving…' : `Approve (${selectedArticleIds.size})`}
                    </button>
                  </>
                )}
              </div>
              {availableTopicsList.length === 0 ? (
                <div className="articles-sidebar-empty">
                  <p>No available topics. Topics become available after pre-processing.</p>
                </div>
              ) : (
                <ul className="articles-sidebar-list">
                  {availableTopicsList.map((topic) => (
                    <li
                      key={topic.id}
                      className={`articles-sidebar-item ${selectedArticleTopic?.id === topic.id ? 'selected' : ''}`}
                      onClick={() => handleSelectArticleTopic(topic)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedArticleIds.has(topic.id)}
                        onChange={(e) => {
                          e.stopPropagation()
                          handleToggleArticleSelection(topic.id)
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="articles-sidebar-item-text">
                        <span className="articles-sidebar-topic-text">{topic.text}</span>
                        {topic.category && (
                          <span className="articles-sidebar-category">{topic.category}</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="articles-main">
              {selectedArticleTopic ? (
                <div className="articles-detail">
                  <div className="articles-detail-header">
                    <h2>{selectedArticleTopic.text}</h2>
                    <div className="articles-detail-actions">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setSelectedArticleTopic(null)}
                      >
                        Close
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleSaveArticle}
                        disabled={articleSaving}
                      >
                        {articleSaving ? 'Saving…' : 'Save Edits'}
                      </button>
                    </div>
                  </div>
                  <div className="articles-preview-tabs">
                    <button
                      className={`articles-preview-tab ${articlePreviewMode === 'formatted' ? 'active' : ''}`}
                      onClick={() => setArticlePreviewMode('formatted')}
                    >
                      Formatted
                    </button>
                    <button
                      className={`articles-preview-tab ${articlePreviewMode === 'raw' ? 'active' : ''}`}
                      onClick={() => setArticlePreviewMode('raw')}
                    >
                      Raw
                    </button>
                  </div>
                  <div className="articles-edit-area">
                    <textarea
                      className="articles-edit-textarea"
                      value={articleEditContent}
                      onChange={handleArticleEditChange}
                      placeholder="Article markdown content..."
                      spellCheck="false"
                    />
                  </div>
                  <div className="articles-preview-area">
                    <h4>Preview</h4>
                    {articlePreviewMode === 'formatted' ? (
                      <div className="articles-preview-formatted">
                        <ReactMarkdown>{articleEditContent || '_No content_'}</ReactMarkdown>
                      </div>
                    ) : (
                      <pre className="articles-preview-raw">{articleEditContent || '_No content_'}</pre>
                    )}
                  </div>
                </div>
              ) : (
                <div className="articles-placeholder">
                  <p>Select an available topic from the list to edit, preview, and approve.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showDeleteModal && (
        <div className="modal-overlay" onClick={cancelDeleteTopic}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Topic</h3>
            <p>Are you sure you want to delete this topic? This action cannot be undone.</p>
            {topicToDelete && <p className="modal-topic-preview">{topicToDelete.text}</p>}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={cancelDeleteTopic} disabled={deleting}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDeleteTopic} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DevSecOpsWebsiteArticles
